import {
  ar, bg, de, enUS, enGB, es, fr, el, hr, it,
  ko, pt, ptBR, ro, ru, sr, tr, zhCN, zhTW,
} from "date-fns/locale"
import { Locale } from "date-fns"
import { t } from "@lingui/macro"

export const localesMap: Record<string, Locale> = {
  ar,
  bg,
  de,
  "en": enUS,
  "en-us": enUS,
  "en-gb": enGB,
  es,
  fr,
  el,
  hr,
  it,
  ko,
  pt,
  "pt-br": ptBR,
  ro,
  ru,
  sr,
  tr,
  "zh": zhCN,
  "zh-cn": zhCN,
  "zh-tw": zhTW,
} as const

export const mapLanguageToDateFnsLocale = (languageString: string): Locale => {
  const normalizedToLower = languageString.toLowerCase()

  if (localesMap[normalizedToLower]) return localesMap[normalizedToLower]

  const baseLanguage = languageString.slice(0,2)
  if(localesMap[baseLanguage]) return localesMap[baseLanguage]

  return enUS
}

export type LanguageOption = {
  id: string
  label: string
}

export const getLanguageOptions = (): LanguageOption[] => [
  {
    id: "en-EN",
    label: t({ message: "English", id: "language_english" }),
  },
  { id: "de-DE", label: t({ message: "German", id: "language_german" }) },
  { id: "es-ES", label: t({ message: "Spanish", id: "language_spanish" }) },
  { id: "fr-FR", label: t({ message: "French", id: "language_french" }) },
  { id: "it-IT", label: t({ message: "Italian", id: "language_italian" }) },
  { id: "ko-KR", label: t({ message: "Korean", id: "language_korean" }) },
  {
    id: "pt-PT",
    label: t({ message: "Portuguese", id: "language_portuguese" }),
  },
  {
    id: "hr-HR",
    label: t({ message: "Croatian", id: "language_croatian" }),
  },
  { id: "ru-RU", label: t({ message: "Russian", id: "language_russian" }) },
  {
    id: "ro-RO",
    label: t({ message: "Romanian", id: "language_romanian" }),
  },
  {
    id: "bg-BG",
    label: t({ message: "Bulgarian", id: "language_bulgarian" }),
  },
  { id: "gr-GR", label: t({ message: "Greek", id: "language_greek" }) },
  { id: "sr-SR", label: t({ message: "Serbian", id: "language_serbian" }) },
  { id: "tr-TR", label: t({ message: "Turkish", id: "language_turkish" }) },
  { id: "ar-AR", label: t({ message: "Arabic", id: "language_arabic" }) },
  { id: "zh-ZH", label: t({ message: "Chinese", id: "language_chinese" }) },
]

// Maps ISO 639-1 codes to our language option prefixes where they differ
export const LANGUAGE_ALIASES: Record<string, string> = {
  el: "gr",
}

const ISO_CODE_BY_LANGUAGE = Object.fromEntries(
  Object.entries(LANGUAGE_ALIASES).map(([iso, language]) => [language, iso]),
)

// Value for document.documentElement.lang. Our catalog codes are not all valid
// BCP-47 subtags, and anything Chrome cannot parse counts as undeclared.
export const toHtmlLang = (language: string): string => {
  const isoCode = ISO_CODE_BY_LANGUAGE[language] ?? language
  return /^[a-z]{2}$/.test(isoCode) ? isoCode : "en"
}

const buildLanguageIdByPrefix = (): Record<string, string> => {
  const map: Record<string, string> = {}
  for (const { id } of getLanguageOptions()) {
    const prefix = id.slice(0, 2).toLowerCase()
    map[prefix] = id
  }
  return map
}

const resolvePrefix = (locale: string): string => {
  const prefix = locale.slice(0, 2).toLowerCase()
  return LANGUAGE_ALIASES[prefix] ?? prefix
}

/**
 * Resolves the 2-letter locale code from a priority chain:
 * userLanguage > appDefLanguage > browserLanguage > "en"
 *
 * Applies alias mapping (e.g. "el" → "gr").
 */
export const resolveLanguage = (
  userLanguage?: string | null,
  appDefLanguage?: string | null,
  browserLanguage?: string,
): string => {
  if (userLanguage) return resolvePrefix(userLanguage)
  if (appDefLanguage) return resolvePrefix(appDefLanguage)
  if (browserLanguage) return resolvePrefix(browserLanguage)
  return "en"
}

export const getDefaultLanguageId = (
  preferredLanguage?: string | null,
): string => {
  const idByPrefix = buildLanguageIdByPrefix()

  if (preferredLanguage) {
    const prefix = resolvePrefix(preferredLanguage)
    if (idByPrefix[prefix]) return idByPrefix[prefix]
  }

  const browserPrefix = resolvePrefix(navigator.language ?? "en")
  return idByPrefix[browserPrefix] ?? "en-EN"
}
